import {
  DiscordLogoIcon,
  GitHubLogoIcon,
  GlobeIcon,
  LinkedInLogoIcon,
  MediumLogo,
  RedditLogoSolidIcon,
  Separator,
  TelegramLogoIcon,
  toolkitCx,
  Tooltip,
  TwitterXLogoIcon,
} from "@sundae/ui-toolkit";
import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import { FC, HTMLAttributes, memo } from "react";

import { NewspaperIcon } from "@heroicons/react/24/solid";
import { TARGET_BLANK } from "../../../constants/href.constants";
import { Social, SocialType } from "../../../gql/generated/taste-test.sdk";
import MemoYoutubeIcon from "../../YTIcon/YTIcon";

interface ITasteTestSocials extends HTMLAttributes<HTMLDivElement> {
  socials: Social[];
}

const SocialIcon: FC<Social> = memo(({ name, url }) => {
  return (
    <a
      {...TARGET_BLANK}
      href={url}
      className={cx(
        "grid cursor-pointer place-items-center gap-2",
        toolkitCx.layout.text.muted,
        toolkitCx.layout.transition.colors,
        {
          "hover:!text-[#229ED9]": name === SocialType.Telegram,
          "hover:!text-[#5865f2]": name === SocialType.Discord,
          "hover:!text-[#6e5494]": name === SocialType.Github,
          "hover:!text-[#0077b5]": name === SocialType.LinkedIn,
          "hover:!text-[#00ab6c]": name === SocialType.Medium,
          "hover:!text-[#ff4500]": name === SocialType.Reddit,
          "hover:!text-[#1da1f2]": name === SocialType.Twitter,
          "hover:!text-[#FF0000]": name === SocialType.Youtube,
          "hover:!text-primary": [
            SocialType.Website,
            SocialType.Whitepaper,
            SocialType.Other,
          ].includes(name),
        }
      )}
    >
      {name === SocialType.Telegram && <TelegramLogoIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Discord && <DiscordLogoIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Github && <GitHubLogoIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.LinkedIn && <LinkedInLogoIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Medium && <MediumLogo className="h-4 w-4 fill-current" />}
      {name === SocialType.Reddit && <RedditLogoSolidIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Twitter && <TwitterXLogoIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Website && <GlobeIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Whitepaper && <NewspaperIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Youtube && <MemoYoutubeIcon className="h-4 w-4 fill-current" />}
      {name === SocialType.Other && <GlobeIcon className="h-4 w-4 fill-current" />}
    </a>
  );
});

const TasteTestSocials: FC<ITasteTestSocials> = ({ className, socials, ...rest }) => {
  if (isEmpty(socials) ?? !socials) return null;

  return (
    <div {...rest} className={cx("flex items-center gap-4", className)}>
      {socials.map((socialItem, index) => (
        <div className="flex items-center gap-4" key={`${socialItem.name}-${index}`}>
          <Tooltip
            trigger={
              <div>
                <SocialIcon {...socialItem} />
              </div>
            }
            tooltipLabel={socialItem.name === SocialType.Twitter ? "X" : socialItem.name}
          />

          {index === socials.length - 1 ? null : (
            <Separator
              orientation="vertical"
              className={cx("h-4", toolkitCx.layout.background.separator)}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default TasteTestSocials;
